import { isReactive, toRaw } from 'vue';

import i18n from '@/i18n';
import { isRecord } from '@/utils/typeGuards';


type SortType = 'number' | 'date' | 'boolean' | 'string';


const isObjectWithData = (instance: unknown): boolean => {
	if (instance == null) {
		return false;
	}
	if (!isRecord(instance)) {
		return true;
	}
	return !(
		Object.keys(instance).length === 0 &&
		Object.getPrototypeOf(instance) === Object.prototype
	);
};


const updateObject = (target: Record<string, unknown>, source: Record<string, unknown>): void => {
	const keys = Object.keys(target).filter(value => Object.keys(source).includes(value));
	for (const key of keys) {
		target[key] = source[key];
	}
};


/*
findItem(items, value, key) -> T | undefined
findItem(items, value, key, null) -> T | null  (T выводится из items)
findItem(items, value, key, defaultValue) -> T  (T выводится из defaultValue,
items проверяется через NoInfer, поэтому readonly literal-массивы подходят)
Реализация одна по runtime -> (find(...) ?? defaultValue)
*/
function findItem<T, K extends keyof T>(
	items: readonly T[],
	value: T[K],
	key: K,
): T | undefined;
function findItem<T, K extends keyof T>(
	items: readonly T[],
	value: T[K],
	key: K,
	defaultValue: null,
): T | null;
function findItem<T, K extends keyof T>(
	items: readonly NoInfer<T>[],
	value: NoInfer<T>[K],
	key: K,
	defaultValue: T,
): T;
function findItem<T, K extends keyof T>(
	items: readonly T[],
	value: T[K],
	key: K,
	defaultValue?: T | null,
): T | null | undefined {
	return items.find(item => item[key] === value) ?? defaultValue;
}


const sortArrayAccordingToValuesComparator = <T>(valuesOrder: readonly T[]) => (a: T, b: T): number => {
	const indexA = valuesOrder.indexOf(a);
	const indexB = valuesOrder.indexOf(b);
	if (indexA === -1 && indexB === -1) {
		return 0;
	} else if (indexA === -1) {
		return 1;
	} else if (indexB === -1) {
		return -1;
	}
	return indexA - indexB;
};


const sortArrayByFieldAccordingToValuesOrder = <T, K extends keyof T>(
	source: T[],
	field: K,
	valuesOrder: readonly T[K][],
): T[] => {
	const sortedArray = [...source];
	sortedArray.sort((a, b) => {
		const indexA = valuesOrder.indexOf(a[field]);
		const indexB = valuesOrder.indexOf(b[field]);
		if (indexA === -1 && indexB === -1) {
			return 0;
		} else if (indexA === -1) {
			return 1;
		} else if (indexB === -1) {
			return -1;
		}
		return indexA - indexB;
	});
	return sortedArray;
};


const toObject = <T extends object>(value: T): T => {
	if (isReactive(value)) {
		return { ...toRaw(value) };
	}
	return value;
};


const applyFunctionToFields = <T extends object>(
	value: T,
	fields: (keyof T & string)[] | string[],
	func: (item: T, field: string) => void,
): T => {
	const result = { ...value };
	for (const field of fields) {
		func(result, field);
	}
	return result;
};


const getField = (object: unknown, fieldPath: string): unknown => {
	const fields = fieldPath.split('.');
	let result: unknown = object;
	for (const field of fields) {
		if (!isRecord(result)) {
			return null;
		}
		result = result[field];
		if (result == null) {
			return null;
		}
	}
	return result;
};


const getDateTime = (value: unknown): number => {
	if (value instanceof Date) {
		return value.getTime();
	}
	return new Date(String(value)).getTime();
};


const makeComparator = (
	compare: (a: unknown, b: unknown) => number,
	reversed?: boolean,
): (a: unknown, b: unknown) => number => (a, b) => {
	if (a == null && b == null) return 0;
	if (a == null) return reversed ? -1 : 1;
	if (b == null) return reversed ? 1 : -1;
	return reversed ? compare(b, a) : compare(a, b);
};


const typeComparators: Record<SortType, (a: unknown, b: unknown) => number> = {
	number: (a, b) => Number(a) - Number(b),
	boolean: (a, b) => Number(a) - Number(b),
	date: (a, b) => getDateTime(a) - getDateTime(b),
	string: (a, b) => String(a).localeCompare(String(b)),
};


const multiSorter = (sortType?: SortType, reversed?: boolean): (a: unknown, b: unknown) => number =>
	makeComparator(typeComparators[sortType ?? 'string'], reversed);


const sortArray = <T>(source: T[], sorter?: (a: T, b: T) => number): T[] => {
	const sortedArray = [...source];
	sortedArray.sort(sorter ?? ((a, b) => multiSorter('string', false)(a, b)));
	return sortedArray;
};


const objectFieldMultiSorter = (
	field: string,
	sortType?: SortType,
	reversed?: boolean,
) => (a: unknown, b: unknown): number => multiSorter(sortType, reversed)(getField(a, field), getField(b, field));


const formatFractions = (digits: number): (value: number) => string => {
	return value => (new Intl.NumberFormat(i18n.global.locale.value, {
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	})).format(value);
};


function safeTrim(value: unknown, defaultValue: string): string;
function safeTrim(value: unknown): string | null | undefined;
function safeTrim(value: unknown, defaultValue?: string): string | null | undefined {
	if (value == null) {
		if (defaultValue !== undefined) {
			return defaultValue;
		}
		return value;
	}
	return String(value).trim();
}


const valueIsEmpty = (value: unknown): boolean => safeTrim(value, '').length === 0;


const formatMilliMetres = formatFractions(0);
const formatLatLong = (value: number | null | undefined): string => {
	if (value == null) {
		return '—';
	}
	return formatFractions(2)(value);
};
const formatMetres = formatFractions(3);


const linearDependence = (
	argMin: number,
	argMax: number,
	valueMin: number,
	valueMax: number,
): (arg: number) => number => {
	return arg => {
		const kappa = (valueMax - valueMin) / (argMax - argMin);
		const result = kappa * arg + valueMax - kappa * argMax;
		const realMin = Math.min(valueMin, valueMax);
		const realMax = Math.max(valueMin, valueMax);
		return Math.max(Math.min(realMax, result), realMin);
	};
};


const m2mm = (value: number): number => Math.round(value * 1000);
const mm2m = (value: number): number => value / 1000;
const km2m = (value: number): number => value * 1000;
const m2km = (value: number): number => value / 1000;
const intToFloatScale = (value: number): number => value / 1000000;
const mkm2mm = mm2m;


const getRandomString = (length: number): string => {
	const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	let result = '';
	for (let i = 0; i < length; i++) {
		result += characters.charAt(Math.floor(Math.random() * characters.length));
	}
	return result;
};


const MAX_LENGTH = 9;
const trimMountpoint = (item: string): string => {
	if (item.length <= MAX_LENGTH) {
		return item;
	}
	return item.substring(0, MAX_LENGTH) + '...';
};


const isFunction = (functionToCheck: unknown): functionToCheck is (...args: unknown[]) => unknown => (
	Boolean(functionToCheck) && typeof functionToCheck === 'function'
);


const median = (values: readonly number[]): number | null => {
	if (values.length === 0) {
		return null;
	}
	const ordered = [...values].sort((left, right) => left - right);
	const middleIndex = Math.floor(ordered.length / 2);
	if (ordered.length % 2 === 1) {
		return ordered[middleIndex] ?? null;
	}
	const left = ordered[middleIndex - 1];
	const right = ordered[middleIndex];
	if (left == null || right == null) {
		return null;
	}
	return (left + right) / 2;
};


const Utils = {
	isObjectWithData,
	updateObject,
	findItem,
	sortArray,
	sortArrayAccordingToValuesComparator,
	sortArrayByFieldAccordingToValuesOrder,
	toObject,
	applyFunctionToFields,
	getField,
	multiSorter,
	objectFieldMultiSorter,
	safeTrim,
	valueIsEmpty,
	formatFractions,
	formatMilliMetres,
	formatLatLong,
	formatMetres,
	linearDependence,
	m2mm,
	mm2m,
	km2m,
	m2km,
	intToFloatScale,
	mkm2mm,
	getRandomString,
	trimMountpoint,
	isFunction,
	median,
} as const;

export default Utils;
